import React from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  LinearProgress,
  Box,
  Chip,
} from '@mui/material';
import {
  InsertDriveFile,
  Delete,
  CheckCircle,
  Error,
  Lock,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { removeFile } from '../store/slices/uploadSlice';
import { sanitizeText, sanitizeError, sanitizeFileSize } from '../utils/sanitize';

const FileList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const files = useSelector((state: RootState) => state.upload.files);

  if (files.length === 0) {
    return null;
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'error':
        return <Error color="error" />;
      default:
        return <InsertDriveFile />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'default';
      case 'encrypting':
        return 'info';
      case 'uploading':
        return 'primary';
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Files ({files.length})
      </Typography>
      <List>
        {files.map((file) => (
          <ListItem
            key={file.id}
            sx={{
              backgroundColor: 'background.paper',
              mb: 1,
              borderRadius: 1,
              boxShadow: 1,
            }}
            secondaryAction={
              file.status === 'pending' && (
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => dispatch(removeFile(file.id))}
                >
                  <Delete />
                </IconButton>
              )
            }
          >
            <ListItemIcon>{getStatusIcon(file.status)}</ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1">{sanitizeText(file.name)}</Typography>
                  {file.encrypted && (
                    <Lock fontSize="small" sx={{ color: 'grey.600' }} />
                  )}
                  <Chip
                    label={file.status}
                    size="small"
                    color={getStatusColor(file.status)}
                  />
                </Box>
              }
              secondary={
                <>
                  <Typography variant="body2" color="textSecondary">
                    {sanitizeFileSize(file.size)}
                    {file.error && (
                      <Typography component="span" color="error" sx={{ ml: 1 }}>
                        - {sanitizeError(file.error)}
                      </Typography>
                    )}
                  </Typography>
                  {(file.status === 'uploading' || file.status === 'encrypting') && (
                    <LinearProgress
                      variant="determinate"
                      value={file.progress}
                      sx={{ mt: 1 }}
                    />
                  )}
                </>
              }
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default FileList;